// ─────────────────────────────────────────────
//  Double booking — one provider, two rooms, the same minutes.
//  Pure: takes the timeline's flat `placed` cases, returns the clashes.
//
//  WHY THIS IS WORSE THAN AN UNCOVERED ROOM, AND WHY IT IS AN ALARM.
//  An uncovered room announces itself: the board says nobody is in it and
//  somebody goes looking for cover. A double-booked CRNA does the opposite —
//  every one of those rooms LOOKS staffed, so nobody looks for anyone, while the
//  person can physically be in only one of them. The board is quietly asserting
//  something impossible, and the rooms it is wrong about are indistinguishable
//  from the rooms it is right about.
//
//  ⚠️ THE AHP SEAT ONLY. NEVER THE ATTENDING.
//  A supervising doctor covering four rooms at once is the normal, intended
//  shape of this service — flagging it would fire on nearly every row of a
//  healthy board and bury the one signal that means something. Only the person
//  IN the room (ahpId → `crnaKey`: CRNA, AA or resident) is constrained to one
//  room at a time.
// ─────────────────────────────────────────────

const finite = (n) => typeof n === "number" && Number.isFinite(n);

// Half-open intervals: a case ending at 15:00 and one starting at 15:00 do not
// overlap. Rooms are scheduled back to back all day, and treating a shared
// boundary as a clash would flag every ordinary turnover on the board.
function overlaps(a, b) {
  return a.startMin < b.endMin && b.startMin < a.endMin;
}

// A case we can actually judge. Missing or non-numeric times mean we do not
// know when it runs, and a clash asserted from unknown times is a false alarm on
// a card whose whole value is that it only speaks when something is wrong.
function usable(p) {
  return (
    p &&
    p.crnaKey &&
    p.roomId &&
    finite(p.startMin) &&
    finite(p.endMin) &&
    p.endMin > p.startMin
  );
}

/**
 * Every provider standing in two rooms at once.
 *
 * @param placed  timeline's flat cases: { roomId, startMin, endMin, crnaKey, crnaName }
 * @returns [{ key, name, roomIds: [id], pairs: [{ a, b, fromMin, toMin }] }]
 *          `pairs` carries the overlapping WINDOW, so a caller can say when the
 *          clash actually bites rather than implying it lasts all day.
 */
export function doubleBookedProviders(placed = []) {
  const byKey = new Map();
  for (const p of Array.isArray(placed) ? placed : []) {
    if (!usable(p)) continue;
    if (!byKey.has(p.crnaKey)) byKey.set(p.crnaKey, []);
    byKey.get(p.crnaKey).push(p);
  }

  const out = [];
  for (const [key, cases] of byKey) {
    // Sorted so the reported pairs read in the order the day runs.
    const list = [...cases].sort((x, y) => x.startMin - y.startMin);
    const pairs = [];
    const roomIds = new Set();
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const a = list[i];
        const b = list[j];
        // ⚠️ DIFFERENT ROOMS ONLY. Two overlapping cases in the SAME room is a
        // scheduling artifact (a split case, an add-on laid over the original)
        // and says nothing impossible about the person — they are in one room.
        // Reporting it here would send someone hunting for a conflict that does
        // not exist, and teach them to distrust the ones that do.
        if (a.roomId === b.roomId) continue;
        if (!overlaps(a, b)) continue;
        pairs.push({
          a: a.roomId,
          b: b.roomId,
          fromMin: Math.max(a.startMin, b.startMin),
          toMin: Math.min(a.endMin, b.endMin),
        });
        roomIds.add(a.roomId);
        roomIds.add(b.roomId);
      }
    }
    if (pairs.length) {
      out.push({
        key,
        name: list.find((c) => c.crnaName)?.crnaName || "Provider",
        roomIds: Array.from(roomIds),
        pairs,
      });
    }
  }
  // Most-conflicted first: the person in three rooms is a bigger problem than
  // the person in two, and is also the harder one to unpick.
  return out.sort((x, y) => y.roomIds.length - x.roomIds.length);
}

// The rooms a caller must find cover for, once the provider keeps `keepRoomId`.
// Kept here rather than in the UI so the resolve flow and the warning cannot
// disagree about which rooms are actually in question.
export function roomsToReassign(conflict, keepRoomId) {
  const ids = conflict?.roomIds || [];
  if (!keepRoomId) return [...ids];
  return ids.filter((id) => id !== keepRoomId);
}

/**
 * The clashes that still want a decision, at `nowMin`.
 *
 * ⚠️ THE DETECTOR ABOVE IS DELIBERATELY TIMELESS AND MUST STAY THAT WAY. It is
 * also the save-time and drag-time guard (assignmentConflict.js,
 * timelineCommit.js, AssignmentModal.js), and those ask a question about the
 * SCHEDULE — "would this edit put somebody in two rooms" — which the clock has
 * no business answering. A clock inside `doubleBookedProviders` would make an
 * impossible assignment saveable simply because the hours had passed.
 *
 * ⚠️ THIS IS THE "NEEDS YOU NOW" QUESTION, WHICH IS A DIFFERENT ONE. A clash
 * whose overlap ENDED at 15:00 is still a true statement about the day and a
 * false statement about right now: at 15:16 nobody can be sent anywhere, the
 * board runner has no move to make, and the alarm sits there red until
 * midnight. Owner, 2026-09-08, on exactly that: "check the time and you see
 * that rebecca is not in 2 rooms anymore, that alarm... should not be there".
 *
 * ⚠️ FUTURE CLASHES SURVIVE. Dropping everything that is not live this minute
 * would delete the whole point of seeing a 5 PM clash at 2 PM, which is the one
 * version of this problem somebody can still fix calmly. The caller labels them
 * with `startsMin` so a clash that has not begun does not read as one that has.
 *
 * ⚠️ ROOM IDS ARE RE-DERIVED FROM THE SURVIVING PAIRS, never carried over. A
 * provider clashing in three rooms this morning and two this afternoon must
 * report TWO at 13:00 — keeping the original set would put a room on the card
 * that nobody is double-booked in any more, and the Fix flow reads these ids.
 *
 * @param conflicts  output of `doubleBookedProviders`
 * @param nowMin     minute-of-day, or null/undefined on any board that is not
 *                   today. Null keeps EVERYTHING — the fail-closed direction,
 *                   the same rule as `rowClaimsAt(row, null)`: no clock means
 *                   no evidence that anything has finished.
 */
export function liveConflicts(conflicts = [], nowMin = null) {
  const list = Array.isArray(conflicts) ? conflicts : [];
  if (!finite(nowMin)) return list;
  const out = [];
  for (const c of list) {
    // Half-open, matching `overlaps`: a window ending exactly at `nowMin` is over.
    const pairs = (c?.pairs || []).filter((p) => finite(p.toMin) && p.toMin > nowMin);
    if (!pairs.length) continue;
    const roomIds = new Set();
    let startsMin = Infinity;
    for (const p of pairs) {
      roomIds.add(p.a);
      roomIds.add(p.b);
      startsMin = Math.min(startsMin, p.fromMin);
    }
    out.push({
      ...c,
      pairs,
      roomIds: Array.from(roomIds),
      // When the earliest surviving overlap begins. Already under way reads as
      // `null` rather than a past time, so a caller cannot print "from 7 AM"
      // about something that has been true for eight hours.
      startsMin: startsMin > nowMin ? startsMin : null,
    });
  }
  return out;
}
